
  import { defineComponent, onMounted, ref, computed } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { MethodologyObject } from '@/store/modules/Indices/MethodologyModule';
  import { IndicesType } from '@/store/enums/TypeOfIndicesEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'methodology-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('methodologyUpdating'), [
          translate('indices'),
        ]);
      });

      // A "detailed" page (the full Price/Volume methodology document) is
      // identified by its string `key`; an individual section is identified
      // by its numeric `id`. Both share this same hub page.
      const routeKey = route.params.key as string | undefined;
      const isDetailed = !!routeKey;

      const { data } = await store.dispatch(
        isDetailed ? Actions.GET_METHODOLOGY_DETAILED : Actions.GET_METHODOLOGY,
        isDetailed ? routeKey : route.params.id
      );

      const methodology = ref<MethodologyObject>(data.data);

      const formatType = (type: number | string) => {
        const match = Object.values(IndicesType).find(
          (indexType) => indexType.id === Number(type)
        );
        return match ? match.nameEn : String(type ?? '');
      };

      const heading = computed(() =>
        isDetailed
          ? methodology.value.titleEn
          : `${formatType(methodology.value.type)} - ${translate(
              'updateMethodology'
            )}`
      );

      const formatDate = (dateString: string) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB');
      };

      const goBack = () => {
        router.back();
      };

      const goToContent = (web: boolean) => {
        const pageName = isDetailed
          ? web
            ? 'methodology-detailed-content-web-editing'
            : 'methodology-detailed-content-editing'
          : web
          ? 'methodology-content-web-editing'
          : 'methodology-content-editing';
        const params = isDetailed
          ? { key: methodology.value.key }
          : { id: `${methodology.value.id}` };
        router.push({ name: pageName, params });
      };

      const methodologySchema = Yup.object().shape({
        titleAr: Yup.string()
          .required(() => translate('TITLE_AR_IS_REQUIRED_FIELD'))
          .label('Arabic Title'),
        titleEn: Yup.string()
          .required(() => translate('TITLE_EN_IS_REQUIRED_FIELD'))
          .label('English Title'),
      });

      const onSubmitUpdate = async () => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const identifier = isDetailed
          ? { key: methodology.value.key }
          : { id: methodology.value.id };

        await store.dispatch(
          isDetailed
            ? Actions.UPDATE_METHODOLOGY_DETAILED
            : Actions.UPDATE_METHODOLOGY,
          {
            ...identifier,
            data: {
              titleEn: methodology.value.titleEn,
              titleAr: methodology.value.titleAr,
              isActive: methodology.value.isActive,
            },
          }
        );
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_METHODOLOGY'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'methodology-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        methodologySchema,
        goBack,
        goToContent,
        heading,
        formatDate,
        methodology,
        can,
      };
    },
  });
